import React, { useState } from 'react';
import {
  Box,
  Typography,
  styled,
  Button,
  Menu,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Business as WorkspaceIcon, KeyboardArrowDown } from '@mui/icons-material';
import { useUser } from '../../../../context/UserContext';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';
import WorkspaceAvatar from './WorkspaceAvatar';
import { formatWorkspaceName } from '../../../../utils/workspaceUtils';
import { Workspace } from '../../../../context/UserContext';

// Styled components
const WorkspaceButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'workspaceCount',
})<{ workspaceCount: number }>(({ theme, workspaceCount }) => {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.2),
    borderRadius: 8,
    backgroundColor: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: theme.palette.text.primary,
    textTransform: 'none',
    minWidth: 'auto',
    width: 'auto',
    justifyContent: 'flex-start',
    fontSize: '0.875rem',
    fontWeight: 500,
    height: 38,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      boxShadow: 'none',
    },
    '&:focus': {
      boxShadow: 'none',
    },
  };
});

const WorkspaceMenu = styled(Menu, {
  shouldForwardProp: (prop) => prop !== 'itemCount',
})<{ itemCount: number }>(({ theme, itemCount }) => {
  // Calculate width based on number of items
  const getWidth = (count: number) => {
    if (count <= 1) return '250px';
    if (count <= 3) return '450px';
    if (count <= 6) return '650px';
    return '720px';
  };

  return {
    '& .MuiPaper-root': {
      marginTop: theme.spacing(1),
      borderRadius: 12,
      width: getWidth(itemCount),
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 96px)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      border: `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    },
  };
});

const WorkspaceGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2),
  maxHeight: 'calc(100vh - 128px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  justifyContent: 'center',
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    padding: theme.spacing(1.5),
  },
}));

const WorkspaceItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5, 2),
  cursor: 'pointer',
  borderRadius: 8,
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[200]}`,
  minHeight: 80,
  minWidth: 0,
  position: 'relative',
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.12)}`,
  },
  '&.selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    borderColor: theme.palette.primary.main,
    boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.15)}`,
    '& .workspace-name': {
      color: theme.palette.primary.main,
      fontWeight: 700,
    },
    '&::before': {
      content: '"✓"',
      position: 'absolute',
      top: 4,
      right: 4,
      width: 14,
      height: 14,
      backgroundColor: theme.palette.primary.main,
      borderRadius: '50%',
      color: theme.palette.common.white,
      fontSize: '9px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
    },
  },
}));

// Main component
const WorkspaceSelector = () => {
  const { workspaces, selectedWorkspace, setSelectedWorkspace, isUserLoaded, loadingUserInfos } = useUser();
  const { topbarTheme } = useLayoutPreferences();
  const textColor = topbarTheme === 'dark' ? 'grey.400' : 'text.primary';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  // Handle workspace selection change
  const handleWorkspaceChange = (workspace: Workspace) => {
    setSelectedWorkspace(workspace);
    handleClose();
  };

  // Handle menu open/close
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    // Ne pas ouvrir le menu s'il n'y a qu'un seul workspace
    if (workspaces.length <= 1) {
      return;
    }
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  // Show loading state while user infos are loading
  if (loadingUserInfos) {
    return (
      <WorkspaceButton workspaceCount={0} disabled>
        <CircularProgress size={26} thickness={4} sx={{ color: 'primary.main' }} />
        <Skeleton 
          variant="text" 
          width={120} 
          height={20} 
          sx={{ 
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            borderRadius: 1,
          }} 
        />
      </WorkspaceButton>
    );
  }

  // Don't render if user is not loaded
  if (!isUserLoaded) {
    return null;
  }

  // Show loading while selectedWorkspace is being determined
  if (!selectedWorkspace && workspaces.length > 0) {
    return (
      <WorkspaceButton workspaceCount={0} disabled>
        <CircularProgress size={26} thickness={4} sx={{ color: 'primary.main' }} />
        <Skeleton 
          variant="text" 
          width={120} 
          height={20} 
          sx={{ 
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            borderRadius: 1,
          }} 
        />
      </WorkspaceButton>
    );
  }

  // Show placeholder if no workspaces
  if (workspaces.length === 0) {
    return (
      <WorkspaceButton workspaceCount={0} disabled>
        <WorkspaceIcon sx={{ fontSize: 16 }} />
        <Typography variant="body2" color="text.secondary">
          Aucun workspace
        </Typography>
      </WorkspaceButton>
    );
  }

  const displayWorkspace = selectedWorkspace || workspaces[0];
  const hasMultipleWorkspaces = workspaces.length > 1;

  return (
    <Box>
      <WorkspaceButton 
        workspaceCount={workspaces.length}
        onClick={hasMultipleWorkspaces ? handleClick : undefined} 
        endIcon={hasMultipleWorkspaces ? <KeyboardArrowDown sx={{ fontSize: 16 }} /> : null}
        disabled={!hasMultipleWorkspaces}
        sx={{ 
          color: textColor,
          cursor: hasMultipleWorkspaces ? 'pointer' : 'default',
          '&:hover': hasMultipleWorkspaces ? {} : {
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            color: textColor,
            backgroundColor: 'transparent',
          }
        }}
      >
        <WorkspaceAvatar 
          volumeName={displayWorkspace.volumeName}
          shortName={displayWorkspace.shortName}
          size={26}
          fontSize="0.75rem"
          sx={{ border: 'none', boxShadow: 'none' }}
        />
        <Typography variant="body2" sx={{ fontWeight: 500, ml: 0.5, fontSize: '0.875rem' }} noWrap>
          {formatWorkspaceName(displayWorkspace.longName)}
        </Typography>
      </WorkspaceButton>
      
      {hasMultipleWorkspaces && (
        <WorkspaceMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          itemCount={workspaces.length}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        >
        <Box sx={{ p: 2 }}>
          
          <WorkspaceGrid>
            {workspaces.map((workspace) => (
              <WorkspaceItem
                key={workspace.id}
                className={selectedWorkspace?.id === workspace.id ? 'selected' : ''}
                onClick={() => handleWorkspaceChange(workspace)}
              >
                <WorkspaceAvatar 
                  volumeName={workspace.volumeName}
                  shortName={workspace.shortName}
                  size={40}
                  fontSize="0.9rem"
                  sx={{ mb: 1 }}
                />
                <Typography 
                  variant="caption" 
                  className="workspace-name"
                  sx={{ 
                    fontWeight: 500, 
                    fontSize: '0.75rem',
                    color: 'grey.800',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    maxWidth: '100%',
                    wordBreak: 'break-word',
                    hyphens: 'auto',
                  }} 
                >
                  {formatWorkspaceName(workspace.longName)}
                </Typography>
              </WorkspaceItem>
            ))}
          </WorkspaceGrid>
        </Box>
        </WorkspaceMenu>
      )}
    </Box>
  );
};

export default WorkspaceSelector;
